import React, { Suspense, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TalleresProvider } from './context/TalleresContext';
import { useNearScreen } from './hooks/useNearScreen';

// ============================================================================
// COMPONENTES CRÍTICOS (Cargados en bundle inicial)
// ============================================================================
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollReveal from './components/ScrollReveal';

// Componentes de Home (LCP)
import HomeCarousel from './components/HomeCarousel';

// ============================================================================
// CODE SPLITTING: LAZY LOADED COMPONENTS (Cargados bajo demanda)
// ============================================================================
const HeroSection = React.lazy(() => import('./components/HeroSection'));
const Footer = React.lazy(() => import('./components/Footer'));
const CookieConsent = React.lazy(() => import('./components/CookieConsent'));

// Componentes secundarios de Home (Lazy Loaded para mejorar LCP y reducir bundle inicial)
const StatsSection = React.lazy(() => import('./components/StatsSection'));
const TestimonialsSection = React.lazy(() => import('./components/TestimonialsSection'));
const WorkshopsCarousel = React.lazy(() => import('./components/WorkshopsCarousel'));
const AboutSection = React.lazy(() => import('./components/AboutSection'));
const InvolucradosSection = React.lazy(() => import('./components/PartnersSection'));
const HezaCTASection = React.lazy(() => import('./components/HezaCTASection'));

// Páginas de talleres (cargadas solo cuando se acceden)
const Talleres = React.lazy(() => import('./pages/Talleres'));
const TallerCFDIDetalle = React.lazy(() => import('./pages/TallerCFDIDetalle'));
const TallerCONTPAQiDesdeCero = React.lazy(() => import('./pages/TallerCONTPAQiDesdeCero'));
const TallerCONTPAQiNivel2 = React.lazy(() => import('./pages/TallerCONTPAQiNivel2'));
const TallerPortalSAT = React.lazy(() => import('./pages/TallerPortalSAT'));
const TallerDeclaracionesAnuales = React.lazy(() => import('./pages/TallerDeclaracionesAnuales'));
const TallerProcesosEspeciales = React.lazy(() => import('./pages/TallerProcesosEspeciales'));
const TallerNominasBasico = React.lazy(() => import('./pages/TallerNominasBasico'));
const TallerNominasEspeciales = React.lazy(() => import('./pages/TallerNominasEspeciales'));
const TallerNominasCalculosEspeciales = React.lazy(() => import('./pages/TallerNominasCalculosEspeciales'));
const TallerPagosProvisionales = React.lazy(() => import('./pages/TallerPagosProvisionales'));
const TallerHerramientasContables = React.lazy(() => import('./pages/TallerHerramientasContables'));

// Otras páginas principales
const Nosotros = React.lazy(() => import('./pages/Nosotros'));
const ConferencesGallery = React.lazy(() => import('./pages/Conferencias'));
const Registro = React.lazy(() => import('./pages/Registro'));
const Practicas2026A = React.lazy(() => import('./pages/Practicas2026A'));
const Practicas2027A = React.lazy(() => import('./pages/Practicas2026B'));
const RegistroTallerCFDI = React.lazy(() => import('./pages/RegistroTallerCFDI'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = React.lazy(() => import('./pages/TermsConditions'));

// AdminPanel (muy pesado, cargado solo cuando se accede)
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));

// ============================================================================
// LOADING FALLBACK
// ============================================================================
// Ahora usando componente reutilizable en LoadingSpinner.js

/**
 * Componente para forzar el scroll al inicio de forma inmediata 
 * antes de que el navegador pinte el nuevo contenido.
 */
function ScrollToTopExecution() {
  const { pathname } = useLocation();
  
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function Home() {
  const { isNearScreen, fromRef } = useNearScreen({ distance: '100px' });

  return (
    <>
      <HomeCarousel />
      <Suspense fallback={<div style={{ height: '600px', background: 'transparent' }} className="stats-fallback" />}>
        <StatsSection />
      </Suspense>
      <Suspense fallback={<LoadingSpinner />}>
        <HeroSection />
      </Suspense>
      
      {/* El div con la ref detecta la cercanía para cargar el resto de componentes pesados */}
      <div ref={fromRef}>
        {isNearScreen && (
          <Suspense fallback={<LoadingSpinner />}>
            <TestimonialsSection />
            <WorkshopsCarousel />
            <AboutSection />
            <InvolucradosSection />
            <HezaCTASection />
          </Suspense>
        )}
      </div>
    </>
  );
}

function App() {
  return (
    <TalleresProvider>
      <Router>
        <ScrollToTopExecution />
        <ScrollToTop />
        <ScrollReveal />
        <div className="App">
        {/* Fondo geométrico global - Optimizado */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
          pointerEvents: 'none',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FEFEFE 0%, #F6F9F9 100%)'
        }}>
          {/* Solo mantener formas esenciales para reducir carga de renderizado */}
          <svg 
            width="100%" 
            height="100%" 
            viewBox="0 0 1920 1080" 
            preserveAspectRatio="xMidYMid slice"
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ 
              width: '100%', 
              height: '100%',
              opacity: 0.4
            }}
          >
            <circle cx="300" cy="200" r="150" fill="#16A797" opacity="0.05" />
            <circle cx="1700" cy="900" r="200" fill="#92D050" opacity="0.05" />
          </svg>
        </div>
        <Routes>
          <Route path="/admin" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminPanel />
            </Suspense>
          } />
          <Route path="/*" element={
            <>
              <Header />
              <main className="App-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                
                {/* Talleres y detalles - lazy loaded */}
                <Route path="/talleres" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Talleres />
                  </Suspense>
                } />
                <Route path="/taller/contpaqi-desde-0" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerCONTPAQiDesdeCero />
                  </Suspense>
                } />
                <Route path="/taller/contpaqi-nivel-2" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerCONTPAQiNivel2 />
                  </Suspense>
                } />
                <Route path="/taller/portal-sat" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerPortalSAT />
                  </Suspense>
                } />
                <Route path="/taller/procesos-especiales" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerProcesosEspeciales />
                  </Suspense>
                } />
                <Route path="/taller/declaraciones-anuales" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerDeclaracionesAnuales />
                  </Suspense>
                } />
                <Route path="/taller/cfdi-detalle" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerCFDIDetalle />
                  </Suspense>
                } />
                <Route path="/taller/nominas-basico/:id" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerNominasBasico />
                  </Suspense>
                } />
                <Route path="/taller/nominas-especiales" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerNominasEspeciales />
                  </Suspense>
                } />
                <Route path="/taller/nominas-calculos-especiales/:id" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerNominasCalculosEspeciales />
                  </Suspense>
                } />
                <Route path="/taller/pagos-provisionales/:id" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerPagosProvisionales />
                  </Suspense>
                } />
                <Route path="/taller/herramientas-practica-contable-fiscal" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerHerramientasContables />
                  </Suspense>
                } />
                <Route path="/taller/contpaqi-desde-0/:id" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TallerCONTPAQiDesdeCero />
                  </Suspense>
                } />
                
                {/* Registro para talleres */}
                <Route path="/registro-taller/:tallerPath" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <RegistroTallerCFDI />
                  </Suspense>
                } />
                
                {/* Otras páginas - lazy loaded */}
                <Route path="/nosotros" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Nosotros />
                  </Suspense>
                } />
                <Route path="/conferencias" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ConferencesGallery />
                  </Suspense>
                } />
                <Route path="/registro" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Registro />
                  </Suspense>
                } />
                <Route path="/practicas-2026a" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Practicas2026A />
                  </Suspense>
                } />
                <Route path="/practicas-2027a" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Practicas2027A />
                  </Suspense>
                } />
                <Route path="/practicas-2026b" element={<Navigate to="/practicas-2027a" replace />} />
                <Route path="/registro-taller-cfdi" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <RegistroTallerCFDI />
                  </Suspense>
                } />
                <Route path="/privacy-policy" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PrivacyPolicy />
                  </Suspense>
                } />
                <Route path="/aviso-privacidad" element={<Navigate to="/privacy-policy" replace />} />
                <Route path="/terms-conditions" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <TermsConditions />
                  </Suspense>
                } />
                <Route path="/terminos-condiciones" element={<Navigate to="/terms-conditions" replace />} />
              </Routes>
              </main>
              <Suspense fallback={null}>
                <Footer />
                <CookieConsent />
              </Suspense>
            </>
          } />
        </Routes>
      </div>
    </Router>
    </TalleresProvider>
  );
}

export default App;
